import React, { useState } from "react";
import styles from "../../assets/styles/ApplyForm/ApplyForm.module.css";

const WHATSAPP_NUMBER = "917417539900";

const countries = [
  "Select country*",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Spain",
  "Italy",
  "Netherlands",
  "Switzerland",
  "Sweden",
  "Norway",
  "Denmark",
  "Belgium",
  "Austria",
  "Ireland",
  "New Zealand",
  "Singapore",
  "Japan",
  "South Korea",
  "India",
  "United Arab Emirates",
  "Brazil",
  "Mexico",
  "Argentina",
  "Chile",
  "Colombia",
  "South Africa",
  "Israel",
  "Russia",
  "Poland",
  "Czech Republic",
  "Portugal",
  "Greece",
  "Turkey",
  "Thailand",
  "Indonesia",
  "Malaysia",
  "Philippines",
  "Vietnam",
  "China",
  "Hong Kong",
  "Taiwan",
];

const courseOptions = [
  "Select course*",
  "100 Hour Yoga Teacher Training",
  "200 Hour Yoga Teacher Training",
  "300 Hour Yoga Teacher Training",
  "500 Hour Yoga Teacher Training",
  "Kundalini Yoga Teacher Training",
  "Yin Yoga Teacher Training",
  "Ayurveda Course",
  "Meditation Course",
  "Other",
];

const durationOptions = [
  "Preferred start date",
  "1st October 2025",
  "1st November 2025",
];

const sourceOptions = [
  "Where did you find us?*",
  "Google",
  "Facebook",
  "Instagram",
  "YouTube",
  "Friend Referral",
  "TripAdvisor",
  "Yoga Alliance",
  "Blog/Article",
  "Other",
];

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  address: "",
  country: countries[0],
  course: courseOptions[0],
  duration: durationOptions[0],
  source: sourceOptions[0],
};

const ApplyForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      nextErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      nextErrors.phone = "Phone number is required";
    }

    if (!formData.country || formData.country === countries[0]) {
      nextErrors.country = "Please select a country";
    }

    if (!formData.course || formData.course === courseOptions[0]) {
      nextErrors.course = "Please select a course";
    }

    if (!formData.source || formData.source === sourceOptions[0]) {
      nextErrors.source = "Please tell us how you found us";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildWhatsAppMessage = (data) => {
    const lines = [
      "*New Apply Form Submission*",
      `*Name:* ${data.name}`,
      `*Email:* ${data.email}`,
      `*Phone:* ${data.phone}`,
      `*Address:* ${data.address || "N/A"}`,
      `*Country:* ${data.country}`,
      `*Course:* ${data.course}`,
      `*Preferred Start Date:* ${
        data.duration && data.duration !== durationOptions[0] ? data.duration : "N/A"
      }`,
      `*Found Us Via:* ${data.source}`,
    ];

    return encodeURIComponent(lines.join("\n"));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      const whatsappMessage = buildWhatsAppMessage(formData);
      const whatsappURL = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;
      window.open(whatsappURL, "_blank");

      setSubmitSuccess("Thank you for your application! We will contact you soon.");
      setFormData(initialFormData);
      setErrors({});
    } catch (error) {
      setSubmitError("Something went wrong while opening WhatsApp. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.topHeading}>
        Become Internationally Certified Yoga Teacher
      </h2>

      <h3 className={styles.subHeading}>Apply Today!</h3>

      {submitError ? <div className={styles.errorText}>{submitError}</div> : null}
      {submitSuccess ? <div className={styles.successText}>{submitSuccess}</div> : null}

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className="row g-3 mb-0">
          <div className="col-6 col-sm-4 col-md-4">
            <div className={styles.inputGroup}>
              <label htmlFor="apply-name">Name</label>
              <input
                id="apply-name"
                name="name"
                type="text"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? styles.errorInput : ""}
              />
              {errors.name ? <span className={styles.fieldError}>{errors.name}</span> : null}
            </div>
          </div>

          <div className="col-6 col-sm-4 col-md-4">
            <div className={styles.inputGroup}>
              <label htmlFor="apply-email">Email Address *</label>
              <input
                id="apply-email"
                name="email"
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? styles.errorInput : ""}
              />
              {errors.email ? <span className={styles.fieldError}>{errors.email}</span> : null}
            </div>
          </div>

          <div className="col-6 col-sm-4 col-md-4">
            <div className={styles.inputGroup}>
              <label htmlFor="apply-phone">Phone *</label>
              <div className={`${styles.phoneInput} ${errors.phone ? styles.errorInput : ""}`}>
                <span className={styles.flag}>+91</span>
                <input
                  id="apply-phone"
                  name="phone"
                  type="tel"
                  placeholder="Enter Mobile Number"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              {errors.phone ? <span className={styles.fieldError}>{errors.phone}</span> : null}
            </div>
          </div>

          <div className="col-6 col-sm-4 col-md-4">
            <div className={styles.inputGroup}>
              <label htmlFor="apply-address">Street Address</label>
              <input
                id="apply-address"
                name="address"
                type="text"
                placeholder="Enter Full Address"
                value={formData.address}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="col-6 col-sm-4 col-md-4">
            <div className={styles.inputGroup}>
              <label htmlFor="apply-country">Country</label>
              <select
                id="apply-country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={errors.country ? styles.errorInput : ""}
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
              {errors.country ? <span className={styles.fieldError}>{errors.country}</span> : null}
            </div>
          </div>

          <div className="col-6 col-sm-4 col-md-4">
            <div className={styles.inputGroup}>
              <label htmlFor="apply-course">Select Course</label>
              <select
                id="apply-course"
                name="course"
                value={formData.course}
                onChange={handleChange}
                className={errors.course ? styles.errorInput : ""}
              >
                {courseOptions.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
              {errors.course ? <span className={styles.fieldError}>{errors.course}</span> : null}
            </div>
          </div>

          <div className="col-6 col-sm-6">
            <div className={styles.inputGroup}>
              <label htmlFor="apply-duration">Duration</label>
              <select
                id="apply-duration"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
              >
                {durationOptions.map((duration) => (
                  <option key={duration} value={duration}>
                    {duration}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-6 col-sm-6">
            <div className={styles.inputGroup}>
              <label htmlFor="apply-source">Where did you find us?</label>
              <select
                id="apply-source"
                name="source"
                value={formData.source}
                onChange={handleChange}
                className={errors.source ? styles.errorInput : ""}
              >
                {sourceOptions.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
              {errors.source ? <span className={styles.fieldError}>{errors.source}</span> : null}
            </div>
          </div>
        </div>

        <button type="submit" className={styles.submitBtn} disabled={loading}>
          {loading ? "Sending..." : "Send Message"}
        </button>
      </form>
    </div>
  );
};

export default ApplyForm;